import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getSalaryComponents, createSalaryComponent, updateSalaryComponent, deleteSalaryComponent,
  getSalaryTemplates, createSalaryTemplate, updateSalaryTemplate, deleteSalaryTemplate,
  previewSalary
} from '../services/api';
import { Plus, X, Edit2, Trash2, ChevronDown, ChevronUp, Eye, DollarSign } from 'lucide-react';
import toast from 'react-hot-toast';

const typeColor = (t) =>
  t === 'earning' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600';

const calcLabel = (c) =>
  c === 'fixed'               ? 'Fixed ₹' :
  c === 'percentage_of_basic' ? '% of Basic' :
  '% of Gross';

// ── Component Form Modal ──────────────────
const ComponentModal = ({ comp, onClose }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    name          : comp?.name           || '',
    code          : comp?.code           || '',
    component_type: comp?.component_type || 'earning',
    calc_type     : comp?.calc_type      || 'percentage_of_basic',
    value         : comp?.value          || '',
    is_taxable    : comp?.is_taxable     || false,
    description   : comp?.description    || '',
    sequence      : comp?.sequence       || 0
  });

  const mutation = useMutation({
    mutationFn: (data) => comp ? updateSalaryComponent(comp.id, data) : createSalaryComponent(data),
    onSuccess: () => {
      toast.success(comp ? 'Component updated' : 'Component created');
      queryClient.invalidateQueries(['salary-components']);
      onClose();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to save component')
  });

  const handle = (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm({ ...form, [e.target.name]: val });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-semibold text-gray-900">
            {comp ? 'Edit Component' : 'New Salary Component'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); mutation.mutate({ ...form, value: parseFloat(form.value), sequence: parseInt(form.sequence) }); }}
          className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Component Name *</label>
              <input name="name" value={form.name} onChange={handle} required
                placeholder="e.g. Basic Salary"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Code *</label>
              <input name="code" value={form.code} onChange={handle} required
                placeholder="e.g. BASIC"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 uppercase" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
              <select name="component_type" value={form.component_type} onChange={handle}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="earning">Earning</option>
                <option value="deduction">Deduction</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Calculation *</label>
              <select name="calc_type" value={form.calc_type} onChange={handle}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="percentage_of_basic">% of Basic</option>
                <option value="percentage_of_gross">% of Gross</option>
                <option value="fixed">Fixed Amount (₹)</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Value ({form.calc_type === 'fixed' ? '₹ Amount' : '% Percentage'}) *
              </label>
              <input name="value" type="number" step="0.01" value={form.value} onChange={handle} required min="0"
                placeholder={form.calc_type === 'fixed' ? 'e.g. 1600' : 'e.g. 40'}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sequence</label>
              <input name="sequence" type="number" value={form.sequence} onChange={handle} min="0"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <input type="checkbox" id="is_taxable" name="is_taxable" checked={form.is_taxable} onChange={handle}
              className="w-4 h-4 text-blue-600 rounded" />
            <label htmlFor="is_taxable" className="text-sm text-gray-700">Taxable component</label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input name="description" value={form.description} onChange={handle}
              placeholder="Optional note"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={mutation.isPending}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {mutation.isPending ? 'Saving...' : comp ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Template Form Modal ───────────────────
const TemplateModal = ({ tmpl, components, onClose }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    name           : tmpl?.name            || '',
    description    : tmpl?.description     || '',
    worker_category: tmpl?.worker_category || ''
  });
  const [selected, setSelected] = useState(
    tmpl?.components?.map(c => ({
      component_id: c.component_id,
      value       : c.value ?? '',
      sequence    : c.sequence ?? 0
    })) || []
  );

  const mutation = useMutation({
    mutationFn: (data) => tmpl ? updateSalaryTemplate(tmpl.id, data) : createSalaryTemplate(data),
    onSuccess: () => {
      toast.success(tmpl ? 'Template updated' : 'Template created');
      queryClient.invalidateQueries(['salary-templates']);
      onClose();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to save template')
  });

  const toggleComponent = (comp) => {
    const exists = selected.find(s => s.component_id === comp.id);
    if (exists) {
      setSelected(selected.filter(s => s.component_id !== comp.id));
    } else {
      setSelected([...selected, { component_id: comp.id, value: '', sequence: selected.length }]);
    }
  };

  const updateValue = (compId, val) => {
    setSelected(selected.map(s => s.component_id === compId ? { ...s, value: val } : s));
  };

  const earnings   = components.filter(c => c.component_type === 'earning');
  const deductions = components.filter(c => c.component_type === 'deduction');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-semibold text-gray-900">
            {tmpl ? 'Edit Template' : 'New Salary Template'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); mutation.mutate({ ...form, components: selected.map(s => ({ ...s, value: s.value !== '' ? parseFloat(s.value) : null })) }); }}
          className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Template Name *</label>
              <input name="name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required
                placeholder="e.g. Blue Collar Standard"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Worker Category</label>
              <select value={form.worker_category} onChange={e => setForm({ ...form, worker_category: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">All Categories</option>
                <option value="blue_collar">Blue Collar</option>
                <option value="white_collar">White Collar</option>
                <option value="specialized">Specialized</option>
                <option value="technical">Technical</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="Optional description"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>

          {/* Component Selection */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-3">Select Components</p>
            <div className="space-y-4">
              {[{ label: 'Earnings', list: earnings }, { label: 'Deductions', list: deductions }].map(group => (
                <div key={group.label}>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{group.label}</p>
                  <div className="space-y-2">
                    {group.list.map(comp => {
                      const sel = selected.find(s => s.component_id === comp.id);
                      return (
                        <div key={comp.id} className={`flex items-center gap-3 p-3 rounded-xl border transition
                          ${sel ? 'border-blue-200 bg-blue-50' : 'border-gray-100 bg-gray-50'}`}>
                          <input type="checkbox" checked={!!sel} onChange={() => toggleComponent(comp)}
                            className="w-4 h-4 text-blue-600 rounded flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{comp.name}
                              <span className="text-xs text-gray-400 ml-2">{comp.code}</span>
                            </p>
                            <p className="text-xs text-gray-400">{calcLabel(comp.calc_type)} · Default: {comp.value}{comp.calc_type !== 'fixed' ? '%' : ''}</p>
                          </div>
                          {sel && (
                            <div className="flex-shrink-0">
                              <input type="number" step="0.01" value={sel.value} min="0"
                                onChange={e => updateValue(comp.id, e.target.value)}
                                placeholder="Override value"
                                className="w-28 px-2 py-1 border border-blue-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500" />
                              <p className="text-xs text-gray-400 mt-0.5 text-center">leave blank = default</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={mutation.isPending}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {mutation.isPending ? 'Saving...' : tmpl ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Preview Modal ─────────────────────────
const PreviewModal = ({ templates, onClose }) => {
  const [templateId, setTemplateId] = useState('');
  const [gross, setGross]           = useState('');
  const [result, setResult]         = useState(null);
  const [loading, setLoading]       = useState(false);

  const handlePreview = async (e) => {
    e.preventDefault();
    if (!templateId || !gross) return;
    setLoading(true);
    try {
      const res = await previewSalary({ template_id: templateId, gross_salary: parseFloat(gross) });
      setResult(res.data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Preview failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-semibold text-gray-900">Preview Salary Breakdown</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        <form onSubmit={handlePreview} className="space-y-4 mb-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Template *</label>
            <select value={templateId} onChange={e => setTemplateId(e.target.value)} required
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">Select template</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Gross Salary (₹) *</label>
            <input type="number" value={gross} onChange={e => setGross(e.target.value)} required min="1"
              placeholder="e.g. 15000"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <button type="submit" disabled={loading}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            {loading ? 'Calculating...' : 'Calculate Breakdown'}
          </button>
        </form>

        {result && (
          <div className="space-y-3">
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Earnings</p>
              {result.earnings.map(e => (
                <div key={e.code} className="flex justify-between py-1.5 border-b border-gray-100 last:border-0">
                  <span className="text-sm text-gray-600">{e.name}
                    <span className="text-xs text-gray-400 ml-1">({e.value}{e.calc_type !== 'fixed' ? '%' : ''})</span>
                  </span>
                  <span className="text-sm font-medium text-gray-900">₹{Number(e.amount).toLocaleString('en-IN')}</span>
                </div>
              ))}
              <div className="flex justify-between pt-2 mt-1 font-semibold">
                <span className="text-sm text-gray-700">Total Earnings</span>
                <span className="text-sm text-green-600">₹{Number(result.total_earnings).toLocaleString('en-IN')}</span>
              </div>
            </div>

            {result.deductions.length > 0 && (
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Deductions</p>
                {result.deductions.map(d => (
                  <div key={d.code} className="flex justify-between py-1.5 border-b border-gray-100 last:border-0">
                    <span className="text-sm text-gray-600">{d.name}</span>
                    <span className="text-sm font-medium text-red-600">- ₹{Number(d.amount).toLocaleString('en-IN')}</span>
                  </div>
                ))}
                <div className="flex justify-between pt-2 mt-1 font-semibold">
                  <span className="text-sm text-gray-700">Total Deductions</span>
                  <span className="text-sm text-red-600">- ₹{Number(result.total_deductions).toLocaleString('en-IN')}</span>
                </div>
              </div>
            )}

            <div className="bg-blue-50 rounded-xl p-4 flex justify-between items-center">
              <span className="text-sm font-semibold text-gray-800">Net Salary</span>
              <span className="text-xl font-bold text-blue-600">₹{Number(result.net_salary).toLocaleString('en-IN')}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────
const SalaryComponents = () => {
  const queryClient = useQueryClient();
  const [activeTab,    setActiveTab]    = useState('components');
  const [showCompModal, setShowCompModal] = useState(false);
  const [showTmplModal, setShowTmplModal] = useState(false);
  const [showPreview,   setShowPreview]   = useState(false);
  const [editComp,      setEditComp]      = useState(null);
  const [editTmpl,      setEditTmpl]      = useState(null);
  const [expandedTmpl,  setExpandedTmpl]  = useState(null);

  const { data: components = [], isLoading: loadingComps } = useQuery({
    queryKey: ['salary-components'],
    queryFn : () => getSalaryComponents().then(r => r.data.data)
  });

  const { data: templates = [], isLoading: loadingTmpls } = useQuery({
    queryKey: ['salary-templates'],
    queryFn : () => getSalaryTemplates().then(r => r.data.data)
  });

  const deleteCompMutation = useMutation({
    mutationFn: deleteSalaryComponent,
    onSuccess : () => { toast.success('Component deleted'); queryClient.invalidateQueries(['salary-components']); },
    onError   : (err) => toast.error(err.response?.data?.message || 'Cannot delete')
  });

  const deleteTmplMutation = useMutation({
    mutationFn: deleteSalaryTemplate,
    onSuccess : () => { toast.success('Template deactivated'); queryClient.invalidateQueries(['salary-templates']); },
    onError   : (err) => toast.error(err.response?.data?.message || 'Cannot delete')
  });

  const earnings   = components.filter(c => c.component_type === 'earning');
  const deductions = components.filter(c => c.component_type === 'deduction');

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Salary Structure</h1>
          <p className="text-sm text-gray-400 mt-0.5">Manage salary components and templates</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowPreview(true)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50">
            <Eye size={16} />
            Preview
          </button>
          {activeTab === 'components' ? (
            <button onClick={() => { setEditComp(null); setShowCompModal(true); }}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700">
              <Plus size={16} />
              Add Component
            </button>
          ) : (
            <button onClick={() => { setEditTmpl(null); setShowTmplModal(true); }}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700">
              <Plus size={16} />
              New Template
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {[{ id: 'components', label: `Components (${components.length})` },
          { id: 'templates',  label: `Templates (${templates.length})` }].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition
              ${activeTab === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── COMPONENTS TAB ── */}
      {activeTab === 'components' && (
        <div className="space-y-4">
          {loadingComps ? (
            <div className="text-center py-12 text-gray-400">Loading components...</div>
          ) : components.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
              <DollarSign size={32} className="mx-auto text-gray-200 mb-3" />
              <p className="text-gray-400 text-sm">No components yet. Add your first salary component.</p>
            </div>
          ) : (
            [{ label: 'Earnings', list: earnings, color: 'text-green-600' },
             { label: 'Deductions', list: deductions, color: 'text-red-600' }].map(group => (
              group.list.length > 0 && (
                <div key={group.label} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                  <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
                    <h3 className={`text-sm font-semibold ${group.color}`}>{group.label}</h3>
                  </div>
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-50">
                        <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Name</th>
                        <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Code</th>
                        <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Calculation</th>
                        <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Value</th>
                        <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Taxable</th>
                        <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {group.list.map(comp => (
                        <tr key={comp.id} className="hover:bg-gray-50 transition">
                          <td className="px-5 py-3 text-sm font-medium text-gray-900">{comp.name}</td>
                          <td className="px-5 py-3">
                            <span className="text-xs font-mono bg-gray-100 px-2 py-0.5 rounded">{comp.code}</span>
                          </td>
                          <td className="px-5 py-3 text-sm text-gray-600">{calcLabel(comp.calc_type)}</td>
                          <td className="px-5 py-3 text-sm text-gray-900 font-medium">
                            {comp.calc_type === 'fixed'
                              ? `₹${Number(comp.value).toLocaleString('en-IN')}`
                              : `${comp.value}%`}
                          </td>
                          <td className="px-5 py-3">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium
                              ${comp.is_taxable ? 'bg-amber-50 text-amber-600' : 'bg-gray-100 text-gray-500'}`}>
                              {comp.is_taxable ? 'Taxable' : 'Non-taxable'}
                            </span>
                          </td>
                          <td className="px-5 py-3">
                            <div className="flex gap-2">
                              <button onClick={() => { setEditComp(comp); setShowCompModal(true); }}
                                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition">
                                <Edit2 size={14} />
                              </button>
                              <button onClick={() => { if (window.confirm(`Delete "${comp.name}"?`)) deleteCompMutation.mutate(comp.id); }}
                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition">
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            ))
          )}
        </div>
      )}

      {/* ── TEMPLATES TAB ── */}
      {activeTab === 'templates' && (
        <div className="space-y-3">
          {loadingTmpls ? (
            <div className="text-center py-12 text-gray-400">Loading templates...</div>
          ) : templates.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
              <DollarSign size={32} className="mx-auto text-gray-200 mb-3" />
              <p className="text-gray-400 text-sm">No templates yet. Create your first salary template.</p>
            </div>
          ) : (
            templates.map(tmpl => (
              <div key={tmpl.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="flex items-center justify-between p-5">
                  <div className="flex items-center gap-4">
                    <button onClick={() => setExpandedTmpl(expandedTmpl === tmpl.id ? null : tmpl.id)}
                      className="text-gray-400 hover:text-gray-600">
                      {expandedTmpl === tmpl.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </button>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{tmpl.name}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {tmpl.worker_category?.replace('_', ' ') || 'All categories'} ·{' '}
                        {tmpl.components.length} components ·{' '}
                        {tmpl._count?.deployments || 0} deployments
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => { setEditTmpl(tmpl); setShowTmplModal(true); }}
                      className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition">
                      <Edit2 size={14} />
                    </button>
                    <button onClick={() => { if (window.confirm(`Deactivate "${tmpl.name}"?`)) deleteTmplMutation.mutate(tmpl.id); }}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                {expandedTmpl === tmpl.id && (
                  <div className="border-t border-gray-100 px-5 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      {[{ label: 'Earnings',   list: tmpl.components.filter(c => c.component.component_type === 'earning') },
                        { label: 'Deductions', list: tmpl.components.filter(c => c.component.component_type === 'deduction') }
                      ].map(group => group.list.length > 0 && (
                        <div key={group.label}>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{group.label}</p>
                          <div className="space-y-1.5">
                            {group.list.map(item => (
                              <div key={item.id} className="flex justify-between text-sm">
                                <span className="text-gray-600">{item.component.name}</span>
                                <span className="font-medium text-gray-900">
                                  {item.value != null
                                    ? (item.component.calc_type === 'fixed' ? `₹${item.value}` : `${item.value}%`)
                                    : (item.component.calc_type === 'fixed' ? `₹${item.component.value}` : `${item.component.value}%`)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Modals */}
      {showCompModal && (
        <ComponentModal comp={editComp} onClose={() => { setShowCompModal(false); setEditComp(null); }} />
      )}
      {showTmplModal && (
        <TemplateModal tmpl={editTmpl} components={components}
          onClose={() => { setShowTmplModal(false); setEditTmpl(null); }} />
      )}
      {showPreview && (
        <PreviewModal templates={templates} onClose={() => setShowPreview(false)} />
      )}
    </div>
  );
};

export default SalaryComponents;
